import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import api from '../../../api';
import { SelectFieldShell, useSelectFieldChrome } from './fieldChrome';
import { DEFAULT_MAX_OPTIONS } from './AppSelect';

/**
 * Server-searched select. react-select/async underneath, AsyncTomSelect's prop contract
 * on top.
 *
 * Backend contract, unchanged from AsyncTomSelect:
 *   GET {loadUrl}?search={query}&limit=30
 *   → a plain array, or { items: [...] }. Both shapes are in use.
 *
 * Most of what AsyncTomSelect had to do by hand disappears here. Its ~120 lines of
 * applySelection / initialValueAppliedRef machinery existed for one reason: TomSelect
 * silently ignores setValue() for an id it has no registered option for, so a preselected
 * value had to have a placeholder option fabricated and injected before it would display.
 * react-select accepts any {value,label} object as its value, so an edit-mode selection is
 * just an object.
 *
 * Two behaviours are deliberately preserved rather than taking react-select's defaults:
 *
 *  - Options load when the menu OPENS, not on mount. react-select's `defaultOptions: true`
 *    fetches during mount, which on a form carrying several of these would fire a burst of
 *    requests for menus the user may never open. TomSelect's `preload: true` triggered on
 *    open, and that is what this does.
 *  - A query shorter than `minLength` makes no request (TomSelect's `shouldLoad`). The
 *    already-loaded options are filtered locally instead of the list going blank.
 */

const REQUEST_LIMIT = 30;

/** Both response shapes are in the wild: a plain array from /crm/companies/options and
 *  friends, or a paged { items: [] } wrapper. */
export const extractRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const AppAsyncSelect = ({
  name,
  value,
  onChange,
  loadUrl,
  valueField = 'value',
  labelField = 'label',
  searchField = 'label',
  placeholder = 'Search...',
  required = false,
  error = null,
  disabled = false,
  label = '',
  dropdownParent = 'body',
  initialValue = null,
  onItemSelect = null,
  minLength = 2,
  debounceMs = 300,
  maxOptions = DEFAULT_MAX_OPTIONS,
  className = '',
  id,
  'aria-describedby': ariaDescribedBy = null,
}) => {
  const {
    inputId, errorId, selectComponents, styles, screenReaderStatus,
  } = useSelectFieldChrome({
    id, error, ariaDescribedBy, required, maxOptions,
  });

  const toOption = useCallback((raw) => {
    const value = String(raw?.[valueField] ?? '');
    const rawLabel = raw?.[labelField];
    return {
      value,
      // An EMPTY label falls back to the value, not just a null one. Call sites build
      // `initialValue` as `{ value: id, label: '' }` when the saved id is not in the
      // already-fetched options list (see LeadsForm/CompanySection) — treating '' as a
      // usable label renders a blank control on an edit form, which reads as "no client
      // selected" over real data.
      label: String(rawLabel == null || rawLabel === '' ? value : rawLabel),
      raw,
    };
  }, [valueField, labelField]);

  // Everything the component has seen, so a selected value keeps its real label after the
  // menu closes and the option list is replaced by the next search.
  const knownOptionsRef = useRef(new Map());
  const rememberOptions = useCallback((options) => {
    options.forEach((option) => {
      if (option.value) knownOptionsRef.current.set(option.value, option);
    });
  }, []);

  const [preloaded, setPreloaded] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // The flag is set on the way IN as well as out. React 18 StrictMode (which this app
  // mounts under) runs effects mount → cleanup → mount in development: a flag only ever
  // cleared on cleanup stays false for the rest of the component's life, every resolved
  // fetch is then thrown away, and the control sits on "Searching…" forever.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const fetchOptions = useCallback(async (query) => {
    try {
      const res = await api.get(loadUrl, { params: { search: query, limit: REQUEST_LIMIT } });
      const options = extractRows(res?.data).map(toOption);
      rememberOptions(options);
      return options;
    } catch (_err) {
      // A failed lookup must not break the form: the control stays usable and shows its
      // no-results message, exactly as AsyncTomSelect's silent callback([]) did.
      return [];
    }
  }, [loadUrl, toOption, rememberOptions]);

  // Fires on the first menu open. Subsequent opens reuse what was loaded.
  const handleMenuOpen = useCallback(() => {
    if (preloaded !== null || isLoading) return;
    setIsLoading(true);
    fetchOptions('').then((options) => {
      if (!isMountedRef.current) return;
      setPreloaded(options);
      setIsLoading(false);
    });
  }, [preloaded, isLoading, fetchOptions]);

  /**
   * Debounced server search. Resolves rather than rejects on every path — an unresolved
   * promise leaves react-select spinning forever.
   */
  const loadOptions = useCallback((rawInput) => new Promise((resolve) => {
    const query = String(rawInput || '').trim();

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    // Below minLength: no request. Filter what is already loaded, so typing one character
    // narrows the preloaded list instead of emptying it.
    if (query.length > 0 && query.length < minLength) {
      resolve(preloaded || []);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchOptions(query).then((options) => {
        if (!isMountedRef.current) {
          resolve([]);
          return;
        }
        // An empty query is the preload, whose result is worth keeping for the
        // below-minLength case above.
        if (query.length === 0) setPreloaded(options);
        resolve(options);
      });
    }, debounceMs);
  }), [minLength, debounceMs, preloaded, fetchOptions]);

  // initialValue is the edit-mode selection: `{ [valueField]: id, [labelField]: label }`.
  const initialOption = useMemo(
    () => (initialValue && initialValue[valueField] != null ? toOption(initialValue) : null),
    [initialValue, valueField, toOption]
  );

  useEffect(() => {
    if (initialOption) rememberOptions([initialOption]);
  }, [initialOption, rememberOptions]);

  /**
   * The displayed selection. Ordered by how much label information each source has:
   * initialValue, then anything already seen, then the bare value.
   *
   * The last fallback is why a preselected id never renders as an empty control — the
   * failure mode AsyncTomSelect's applySelection existed to prevent.
   */
  const selected = useMemo(() => {
    if (value == null || value === '') return null;
    const asString = String(value);
    if (initialOption && initialOption.value === asString) return initialOption;
    return knownOptionsRef.current.get(asString) || { value: asString, label: asString };
  }, [value, initialOption]);

  const handleChange = useCallback((option) => {
    onChange(name, option?.value ?? '');
    // Sibling fields (phone, address, PIC) are auto-filled from the full row by
    // Expense/Payment/Invoice, so the raw object matters, not just the id.
    if (onItemSelect && option?.raw) onItemSelect(option.raw);
  }, [name, onChange, onItemSelect]);

  /**
   * A query at or above minLength was answered by the server, which already decided what
   * matches — re-filtering locally would hide rows matched on a field the label does not
   * show. Below minLength nothing was requested, so filter locally over `searchField`.
   */
  const searchFields = useMemo(
    () => String(searchField || '').split(',').map((field) => field.trim()).filter(Boolean),
    [searchField]
  );

  const filterOption = useCallback((candidate, rawInput) => {
    const input = String(rawInput || '').trim().toLowerCase();
    if (!input || input.length >= minLength) return true;
    const haystack = [candidate.label, ...searchFields.map((f) => candidate.data?.raw?.[f])]
      .filter((part) => part != null)
      .map((part) => String(part).toLowerCase());
    return haystack.some((part) => part.includes(input));
  }, [minLength, searchFields]);

  const noOptionsMessage = useCallback(({ inputValue }) => {
    const query = String(inputValue || '').trim();
    if (query.length > 0 && query.length < minLength) {
      return `Type ${minLength} characters to search`;
    }
    return 'No results';
  }, [minLength]);

  return (
    <SelectFieldShell
      label={label}
      required={required}
      inputId={inputId}
      error={error}
      errorId={errorId}
      className={className}
    >
      <AsyncSelect
        inputId={inputId}
        name={name}
        classNamePrefix="app-select"
        className={error ? 'is-invalid' : undefined}
        styles={styles}
        components={selectComponents}
        screenReaderStatus={screenReaderStatus}
        value={selected}
        onChange={handleChange}
        loadOptions={loadOptions}
        defaultOptions={preloaded || []}
        onMenuOpen={handleMenuOpen}
        isLoading={isLoading}
        filterOption={filterOption}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={() => 'Searching…'}
        isDisabled={disabled}
        placeholder={placeholder}
        menuPlacement="auto"
        menuPortalTarget={
          dropdownParent != null && typeof document !== 'undefined' ? document.body : null
        }
      />
    </SelectFieldShell>
  );
};

AppAsyncSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  loadUrl: PropTypes.string.isRequired,
  valueField: PropTypes.string,
  labelField: PropTypes.string,
  searchField: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  dropdownParent: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  initialValue: PropTypes.object,
  onItemSelect: PropTypes.func,
  minLength: PropTypes.number,
  debounceMs: PropTypes.number,
  maxOptions: PropTypes.number,
  className: PropTypes.string,
  id: PropTypes.string,
  'aria-describedby': PropTypes.string,
};

export default AppAsyncSelect;
